import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Clock, CheckCircle2, Instagram, Facebook } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import useSeo from "../hooks/useSeo";
import { business } from "../data/content";

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");

export default function Contact() {
  useSeo({
    title: `Contact | Soft Petal Electrolysis, ${business.city}, ${business.state}`,
    description: `Get in touch with Soft Petal Electrolysis in ${business.city}, ${business.state}. Reach us by phone, email, or send a message through our contact form.`,
    path: "/contact",
  });

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    preferredContact: "Email",
    treatmentArea: "",
    message: "",
    consent: false,
    "bot-field": "",
  });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle | submitting | success | error

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required.";
    if (!form.lastName.trim()) e.lastName = "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Please share a few more details (10+ characters).";
    if (!form.consent) e.consent = "Please provide consent to be contacted.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setState("submitting");
    try {
      // Netlify Forms submission, URL-encoded body posted to the site root.
      // Netlify intercepts POSTs that include "form-name" before the SPA redirect.
      const res = await fetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/x-www-form-urlencoded, text/plain, */*",
        },
        body: encode({
          "form-name": "contact",
          ...form,
          consent: form.consent ? "yes" : "no",
        }),
      });
      if (!res.ok) throw new Error(`Netlify Forms returned ${res.status}`);
      setState("success");
      setForm({
        firstName: "", lastName: "", email: "", phone: "",
        preferredContact: "Email", treatmentArea: "", message: "",
        consent: false, "bot-field": "",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Contact form submit failed:", err);
      setState("error");
    }
  };

  const field = (id) => ({
    id,
    name: id,
    value: form[id],
    onChange: (e) =>
      setForm((f) => ({
        ...f,
        [id]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
      })),
  });

  const inputCls =
    "mt-2 w-full rounded-2xl border border-pale-rose bg-warm-white px-4 py-3 font-body text-charcoal placeholder:text-charcoal-soft/50 focus:border-petal focus:outline-none focus:ring-2 focus:ring-petal/30 transition-colors";

  return (
    <div>
      <section className="watercolor-blush py-16 lg:py-14 relative overflow-hidden">
        <FloatingPetals count={6} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Contact"
            title="Let&rsquo;s"
            scriptWord="talk soon"
            subtitle="Share what&rsquo;s on your mind and we&rsquo;ll be in touch. Please avoid sharing sensitive medical details in this form, we&rsquo;ll cover those privately during your consultation."
          />
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-7">
            {state === "success" ? (
              <div
                className="bg-warm-white rounded-3xl p-10 border border-pale-rose/50 shadow-soft text-center"
                data-testid="contact-success"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2 className="mx-auto text-petal" size={32} />
                <h3 className="mt-4 font-serif-display text-3xl">Thank you.</h3>
                <p className="mt-3 font-body text-charcoal-soft">
                  Your message is on its way. We&rsquo;ll get back to you within 1&ndash;2 business days.
                </p>
                <button
                  type="button"
                  className="btn-ghost-petal mt-8"
                  onClick={() => setState("idle")}
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                name="contact"
                method="POST"
                action="/"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                // eslint-disable-next-line react/no-unknown-property
                netlify-honeypot="bot-field"
                onSubmit={onSubmit}
                className="bg-warm-white rounded-3xl p-8 sm:p-10 border border-pale-rose/50 shadow-soft"
                data-testid="contact-form"
                noValidate
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don&rsquo;t fill this out: <input name="bot-field" onChange={field("bot-field").onChange} />
                  </label>
                </p>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstName" className="font-body text-sm text-charcoal">First name</label>
                    <input {...field("firstName")} className={inputCls} data-testid="input-firstName" />
                    {errors.firstName && <p className="mt-1 text-sm text-destructive" data-testid="err-firstName">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="font-body text-sm text-charcoal">Last name</label>
                    <input {...field("lastName")} className={inputCls} data-testid="input-lastName" />
                    {errors.lastName && <p className="mt-1 text-sm text-destructive" data-testid="err-lastName">{errors.lastName}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label htmlFor="email" className="font-body text-sm text-charcoal">Email</label>
                    <input type="email" {...field("email")} className={inputCls} data-testid="input-email" />
                    {errors.email && <p className="mt-1 text-sm text-destructive" data-testid="err-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="font-body text-sm text-charcoal">Phone number</label>
                    <input type="tel" {...field("phone")} className={inputCls} data-testid="input-phone" />
                    {errors.phone && <p className="mt-1 text-sm text-destructive" data-testid="err-phone">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label htmlFor="preferredContact" className="font-body text-sm text-charcoal">Preferred contact method</label>
                    <select {...field("preferredContact")} className={inputCls} data-testid="input-preferredContact">
                      <option>Email</option>
                      <option>Phone</option>
                      <option>Text</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="treatmentArea" className="font-body text-sm text-charcoal">Treatment area <span className="text-charcoal-soft/60">(optional)</span></label>
                    <input {...field("treatmentArea")} className={inputCls} placeholder="e.g., chin, upper lip, underarms" data-testid="input-treatmentArea" />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="message" className="font-body text-sm text-charcoal">Message</label>
                  <textarea
                    {...field("message")}
                    rows="5"
                    className={inputCls}
                    placeholder="Tell us a little about what you&rsquo;re hoping for."
                    data-testid="input-message"
                  />
                  {errors.message && <p className="mt-1 text-sm text-destructive" data-testid="err-message">{errors.message}</p>}
                </div>

                <label className="mt-6 flex items-start gap-3 font-body text-sm text-charcoal-soft cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-petal"
                    data-testid="input-consent"
                  />
                  I consent to Soft Petal Electrolysis contacting me about my inquiry. I understand this form is not for sharing medical or health information.
                </label>
                {errors.consent && <p className="mt-1 text-sm text-destructive" data-testid="err-consent">{errors.consent}</p>}

                <button type="submit" className="btn-petal mt-8" disabled={state === "submitting"} data-testid="contact-submit">
                  {state === "submitting" ? "Sending..." : "Send Message"}
                </button>

                {state === "error" && (
                  <p className="mt-4 text-sm text-destructive" data-testid="contact-error">
                    Something went wrong. Please try again, or email us directly.
                  </p>
                )}

                <p className="mt-6 text-xs font-body text-charcoal-soft/80">
                  Your privacy matters. We&rsquo;ll never share your details. See our{" "}
                  <a href="/legal/privacy" className="text-petal hover:underline">Privacy Policy</a>.
                </p>
              </form>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-blush/60 rounded-3xl p-8 border border-pale-rose/50">
              <h3 className="font-serif-display text-2xl">{business.name}</h3>
              <ul className="mt-6 space-y-4 font-body text-sm text-charcoal">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-petal mt-0.5" />
                  <span>{business.address}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={16} className="text-petal mt-0.5" />
                  <a href={business.phoneHref} className="hover:text-petal" data-testid="contact-phone">{business.phone}</a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={16} className="text-petal mt-0.5" />
                  <a href={business.emailHref} className="hover:text-petal" data-testid="contact-email">{business.email}</a>
                </li>
              </ul>
              <div className="mt-6 flex gap-3">
                <a href={business.social.instagram} target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="w-9 h-9 rounded-full flex items-center justify-center bg-white text-petal hover:bg-petal hover:text-white transition-colors">
                  <Instagram size={16} />
                </a>
                <a href={business.social.facebook} target="_blank" rel="noreferrer noopener" aria-label="Facebook" className="w-9 h-9 rounded-full flex items-center justify-center bg-white text-petal hover:bg-petal hover:text-white transition-colors">
                  <Facebook size={16} />
                </a>
              </div>
            </div>

            <div className="bg-warm-white rounded-3xl p-8 border border-pale-rose/50 shadow-soft">
              <div className="flex items-center gap-2 text-petal font-body text-xs tracking-[0.3em] uppercase">
                <Clock size={14} /> Studio Hours
              </div>
              <ul className="mt-5 space-y-2.5 font-body text-sm">
                {business.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between">
                    <span className="text-charcoal">{h.day}</span>
                    <span className="text-charcoal-soft">{h.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl overflow-hidden aspect-video border border-pale-rose/50 shadow-soft" data-testid="map-placeholder">
              <iframe
                title="Soft Petal Electrolysis, Garden City NY map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-73.66%2C40.71%2C-73.61%2C40.74&layer=mapnik&marker=40.7268%2C-73.6343"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
